import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Filter } from 'lucide-react';
import { SPECIALTIES, ALL_MARKET_NAMES, ALL_MARKET_CODES } from '@hc/shared';
import { fetchSpecialtyCoverage } from '../../api/screener-client.js';
import { PanelFrame } from '../panel-frame.js';
import { MARKET_PROVENANCE } from '../market-provenance.js';
import type { PanelId, TabId } from '../store.js';

// Sources — the platform's data provenance, FILTERABLE BY MARKET. Lifted out of
// the Methodology panel so the source roster is a first-class reference. Each
// figure traces to an official source carried with its vintage + coverage; this
// panel is the authoritative register of where everything comes from, per market
// plus the cross-market spine that applies everywhere.
//
// The roster covers ALL 27 served markets (market-provenance.ts). Two pipeline
// kinds: 'pipeline' markets are ingested directly from the national register by
// our own services; 'commissioned' markets are research-fleet datasets built
// from the named registers and loaded via the staged merge with join-verified
// registry ids.

const FILTER_OPTIONS = [
  { code: 'all', name: 'All markets' },
  ...MARKET_PROVENANCE.map((m) => ({ code: m.code as string, name: m.name })),
] as const;

function Defn({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="src-defn">
      <span className="src-defn-t">{term}</span>
      <span className="src-defn-d">{children}</span>
    </div>
  );
}

// Market filter — the Screener's filter-button pattern: a bordered button (Filter
// icon + current selection, accent-tinted when narrowed) that opens a portaled
// dropdown menu. Portaled (fixed) so the panel's overflow:auto can't clip it, and
// it tracks the panel when floated/scrolled.
function MarketFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = value !== 'all';
  const current = FILTER_OPTIONS.find((o) => o.code === value)?.name ?? 'All markets';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Filter sources by market"
        className="src-filterbtn"
        data-active={active}
      >
        <Filter size={12} />
        <span>{current}</span>
        <ChevronDown size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="src-menu"
            style={{ top: rect.top, left: rect.left, minWidth: Math.max(rect.width, 180) }}
          >
            {FILTER_OPTIONS.map((o) => {
              const sel = o.code === value;
              return (
                <button
                  key={o.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sel}
                  className={'src-menuitem' + (sel ? ' on' : '')}
                  onClick={() => {
                    onChange(o.code);
                    setOpen(false);
                  }}
                >
                  <span className="src-menucheck">{sel && <Check size={12} />}</span>
                  {o.name}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

export function SourcesPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  // 'all' (every market) or a single ISO code. The cross-market spine shows in
  // both views — it applies to every market regardless of the filter.
  const [market, setMarket] = useState<string>('all');
  // Two views: the provenance register (Sources) and the fine sub-segment
  // detectability matrix (Coverage) — the honesty layer.
  const [view, setView] = useState<'sources' | 'coverage'>('sources');
  const shown =
    market === 'all' ? MARKET_PROVENANCE : MARKET_PROVENANCE.filter((m) => m.code === market);

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="src-root">
        <style>{SRC_CSS}</style>

        <div className="src-top">
          <div className="src-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'sources'}
              className={'src-tab' + (view === 'sources' ? ' on' : '')}
              onClick={() => setView('sources')}
            >
              Sources
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'coverage'}
              className={'src-tab' + (view === 'coverage' ? ' on' : '')}
              onClick={() => setView('coverage')}
            >
              Detectability
            </button>
          </div>
          {view === 'sources' && <MarketFilter value={market} onChange={setMarket} />}
          <p className="src-intro">
            {view === 'sources'
              ? 'Every figure traces to an official source, carried with its vintage and coverage. Sources refresh roughly annually; a recompute re-runs on each refresh.'
              : 'What each market’s sources can DETECT for the fine sub-segment tier, and whether we’ve populated it yet. We never assert beyond the evidence.'}
          </p>
        </div>

        {view === 'coverage' && <CoverageView />}

        {view === 'sources' && (
          <div className="src-body">
            {shown.map((m) => (
              <div key={m.code} className="src-market">
                <div className="src-mh">
                  <span>{m.name}</span>
                  <span
                    className={'src-badge ' + (m.kind === 'pipeline' ? 'complete' : 'capacity')}
                  >
                    {m.kind === 'pipeline' ? 'register pipeline' : 'commissioned dataset'}
                  </span>
                </div>
                <Defn term="Company & ownership">{m.company}</Defn>
                <Defn term="Demographics & demand">{m.demographics}</Defn>
              </div>
            ))}

            {/* The cross-market spine — applies to every market, shown in all views. */}
            <div className="src-h">Cross-market inputs</div>
            <Defn term="Revenue model">
              Filed turnover (where disclosed), employees and total assets, plus per-market{' '}
              <span className="src-mono">model_export</span> files carrying the calibrated
              subsegment confidence (confidence_P) the platform applies.
            </Defn>
            <Defn term="Demand projections">
              Official population projections — Eurostat EUROPOP2025 at NUTS-2 for 26 markets, ONS
              2022-based for the UK — with the 80+ cohort as the care-need proxy. All 27 markets are
              projected regionally.
            </Defn>
            <Defn term="Quality ratings">
              National inspection regimes normalised to a cross-market quality tier (CQC & devolved
              UK bodies, HAS, IVO, IGJ, ANMCS, A-IQI, …) with a per-market trajectory where rating
              history exists.
            </Defn>
            <Defn term="Workforce supply">
              OECD Health at a Glance, Eurostat (density, vacancy) and WHO GHO for the cross-market
              spine; OECD remuneration; national / NUTS-2 regional registers for native geography.
            </Defn>
            <Defn term="Pet populations">FEDIAF — veterinary demand sizing.</Defn>
            {/* ODbL attribution for the DATA, not the basemap. The Leaflet TileLayer
                attribution on every map covers CARTO's rendered tiles only; it does not
                disclose that a share of our stored COORDINATES is itself OSM-derived.
                Operating on the Produced Work reading of ODbL (ruling 2026-08-09), which
                requires attribution rather than share-alike — so the attribution has to
                actually exist, and this is where it lives. Keep it whenever OSM-derived
                geocoding is added; three pending loads (GR, BG, DE) do exactly that. */}
            <Defn term="Geocoding">
              Official national address registers and health-facility registers where they publish
              coordinates. Where they do not, a share of site coordinates is derived from{' '}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
                OpenStreetMap
              </a>
              , made available under the{' '}
              <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noreferrer">
                Open Database Licence
              </a>
              . Coordinate precision is carried per site (rooftop, address, street, postcode,
              neighbourhood, town) and shown wherever a location is displayed; it is never inferred
              upward.
            </Defn>

            <div className="src-note">
              Honesty register: some income bases are per-person or decile rather than £/household,
              and a few grains do not publish sex or age at the finest level — these caveats are
              surfaced where the data is shown, not hidden. Commissioned-market datasets load only
              through the staged merge with join-verified registry ids; a figure that cannot be
              keyed to its national register is rejected, not guessed. The roster above is the
              authoritative provenance.
            </div>
          </div>
        )}
      </div>
    </PanelFrame>
  );
}

// ── Coverage view — fine sub-segment (specialty) detectability matrix ─────────
// Rows = markets, columns = the 10 fine specialties. Each cell: live (populated
// now) · a detection grade (detectable, not yet populated) · — (no free signal).
const SPECIALTY_SHORT: Record<string, string> = {
  dialysis: 'Dialysis',
  oncology: 'Oncology',
  radiotherapy: 'Radioth.',
  fertility_ivf: 'Fertility',
  ophthalmology: 'Ophthal.',
  imaging: 'Imaging',
  pathology: 'Pathol.',
  nursing_home: 'Nursing',
  residential_care: 'Resid.',
  assisted_living: 'Assisted',
};
const GRADE_ABBR: Record<string, string> = {
  native: 'N',
  register: 'R',
  register_friction: 'R*',
  name: 'nm',
  none: '—',
};

function CoverageView() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['specialty-coverage'],
    queryFn: fetchSpecialtyCoverage,
  });
  if (isLoading) return <div className="cov-msg">Loading coverage…</div>;
  if (isError || !data) return <div className="cov-msg">Coverage unavailable.</div>;

  const pop = new Set(data.populated);
  const cap = new Map(data.capability.map((c) => [`${c.market}:${c.specialty}`, c]));
  // Rows = ALL 27 markets (the authoritative launch roster), sorted by name — NOT
  // just the 25 in the capability matrix. Portugal + Slovenia have no capability
  // rows yet (excluded from the 25-market scoping), so they render as "not yet
  // scoped" rather than being dropped.
  const markets = [...ALL_MARKET_CODES].sort((a, b) =>
    ALL_MARKET_NAMES[a].localeCompare(ALL_MARKET_NAMES[b]),
  );

  return (
    <div className="cov-body">
      <div className="cov-legend">
        <span>
          <i className="cov-dot live" /> live
        </span>
        <span>
          <i className="cov-dot det" /> detectable (grade), not populated
        </span>
        <span>
          <i className="cov-dot none" /> no free signal
        </span>
        <span>
          <i className="cov-dot unscoped" /> not yet scoped (PT · SI)
        </span>
        <span className="cov-key">N native · R register · R* friction · nm name</span>
      </div>
      <div className="cov-scroll">
        <table className="cov-table">
          <thead>
            <tr>
              <th className="cov-mk">Market</th>
              {SPECIALTIES.map((s) => (
                <th key={s.token} title={s.label}>
                  {SPECIALTY_SHORT[s.token] ?? s.token}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {markets.map((mk) => (
              <tr key={mk}>
                <td className="cov-mk">
                  {ALL_MARKET_NAMES[mk as keyof typeof ALL_MARKET_NAMES] ?? mk}
                </td>
                {SPECIALTIES.map((s) => {
                  const c = cap.get(`${mk}:${s.token}`);
                  const live = pop.has(`${mk}:${s.token}`);
                  // No capability row at all = detectability not yet scoped (PT/SI),
                  // distinct from an assessed "no free signal".
                  const unscoped = !c;
                  const hidden = c && (c.projectedState === 'hidden' || c.ceilingGrade === 'none');
                  const state = live ? 'live' : unscoped ? 'unscoped' : hidden ? 'none' : 'det';
                  const label = live
                    ? '●'
                    : unscoped
                      ? '?'
                      : hidden
                        ? '·'
                        : (GRADE_ABBR[c!.ceilingGrade] ?? '?');
                  const title = live
                    ? `Live — populated now (${c?.detectionSource ?? 'derived'})`
                    : unscoped
                      ? 'Detectability not yet scoped for this market'
                      : hidden
                        ? 'No free signal in this market'
                        : `Detectable (${c!.ceilingGrade}) — not yet populated · ${c!.detectionSource ?? ''}`;
                  return (
                    <td key={s.token} className={'cov-cell ' + state} title={title}>
                      {label}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SRC_CSS = `
.src-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.src-top { display:flex; align-items:center; gap:var(--s-3); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); background:var(--surface); flex-wrap:wrap; }
.src-intro { margin:0; line-height:1.5; color:var(--text-tertiary); font-size:var(--t-meta); flex:1; min-width:240px; }
.src-filterbtn { display:inline-flex; align-items:center; gap:var(--s-1); padding:2px var(--s-2); font:inherit; font-size:var(--t-body); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); cursor:pointer; white-space:nowrap; flex-shrink:0; }
.src-filterbtn:hover { background:var(--hover-subtle); }
.src-filterbtn[data-active="true"] { background:var(--color-accent-100); border-color:var(--accent); }
.src-menu { position:fixed; z-index:9000; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:2px; box-shadow:0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08); padding:4px; max-height:340px; overflow-y:auto; }
.src-menuitem { display:flex; align-items:center; gap:6px; width:100%; font:inherit; font-size:var(--t-body); text-align:left; padding:4px 8px 4px 4px; border:none; background:transparent; color:var(--text-primary); border-radius:2px; cursor:pointer; white-space:nowrap; }
.src-menuitem:hover { background:var(--bg-sunken); }
.src-menuitem.on { background:var(--color-accent-100); font-weight:600; }
.src-menucheck { display:inline-flex; width:16px; flex-shrink:0; color:var(--accent); }
.src-body { flex:1; overflow:auto; padding:var(--s-4) var(--s-3); min-height:0; max-width:760px; }
.src-market { margin-bottom:16px; }
.src-mh { display:flex; align-items:baseline; gap:10px; font-size:var(--t-body); font-weight:700; color:var(--text-primary); margin-bottom:4px; }
.src-badge { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; padding:1px 6px; border-radius:2px; border:1px solid var(--hairline); }
.src-badge.complete { color:var(--positive,#0a7d5a); border-color:color-mix(in srgb, var(--positive,#0a7d5a) 40%, transparent); }
.src-badge.capacity { color:var(--text-tertiary); }
.src-h { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.06em; color:var(--text-secondary); font-weight:700; margin:22px 0 7px; border-top:2px solid var(--text-primary); padding-top:7px; }
.src-defn { display:grid; grid-template-columns:170px 1fr; gap:10px; padding:6px 0; border-bottom:1px solid var(--hairline); line-height:1.5; }
.src-defn-t { font-weight:600; color:var(--text-primary); }
.src-defn-d { color:var(--text-secondary); }
.src-mono { font-family:var(--font-mono,monospace); font-size:0.95em; }
.src-note { margin-top:14px; padding:9px 11px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.5; }
.src-tabs { display:inline-flex; gap:2px; flex-shrink:0; }
.src-tab { font:inherit; font-size:var(--t-body); padding:2px var(--s-2); border:1px solid var(--hairline); background:var(--surface); color:var(--text-secondary); cursor:pointer; border-radius:2px; }
.src-tab:hover { background:var(--hover-subtle); }
.src-tab.on { background:var(--color-accent-100); border-color:var(--accent); color:var(--text-primary); font-weight:600; }
.cov-body { flex:1; overflow:auto; padding:var(--s-3); min-height:0; display:flex; flex-direction:column; gap:var(--s-3); }
.cov-msg { padding:var(--s-4); color:var(--text-tertiary); }
.cov-legend { display:flex; flex-wrap:wrap; align-items:center; gap:14px; font-size:var(--t-meta); color:var(--text-secondary); }
.cov-legend span { display:inline-flex; align-items:center; gap:5px; }
.cov-dot { width:9px; height:9px; border-radius:50%; display:inline-block; }
.cov-dot.live { background:var(--positive,#0a7d5a); }
.cov-dot.det { background:var(--color-accent-300,#c9a23a); }
.cov-dot.none { background:var(--hairline); }
.cov-dot.unscoped { background:transparent; border:1px dashed var(--text-tertiary); }
.cov-key { color:var(--text-tertiary); font-family:var(--font-mono,monospace); }
.cov-scroll { overflow:auto; border:1px solid var(--hairline); border-radius:2px; }
.cov-table { border-collapse:collapse; font-size:var(--t-meta); white-space:nowrap; }
.cov-table th, .cov-table td { border-bottom:1px solid var(--hairline); border-right:1px solid var(--hairline); padding:3px 6px; text-align:center; }
.cov-table thead th { position:sticky; top:0; background:var(--surface); font-weight:700; color:var(--text-secondary); z-index:1; }
.cov-mk { text-align:left !important; position:sticky; left:0; background:var(--surface); font-weight:600; color:var(--text-primary); z-index:1; }
.cov-cell { font-variant-numeric:tabular-nums; }
.cov-cell.live { background:color-mix(in srgb, var(--positive,#0a7d5a) 16%, transparent); color:var(--positive,#0a7d5a); font-weight:700; }
.cov-cell.det { background:color-mix(in srgb, var(--color-accent-300,#c9a23a) 14%, transparent); color:var(--text-secondary); }
.cov-cell.none { color:var(--text-tertiary); }
.cov-cell.unscoped { color:var(--text-tertiary); opacity:0.5; font-style:italic; }
`;
